import { lazy, Suspense, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import {
  Route,
  Routes as ReactRouterRoutes,
  useParams,
} from 'react-router-dom';

import TopicContext, {
  useTopicContext,
} from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { NoMatch } from 'hb-react/publicoffice/components/Router/NoMatch';

import { resetingTopics } from '../actions/topics';

const TopicShowContainer = lazy(() => import('./TopicShowContainer'));

const TopicsIndexApp = lazy(
  () => import('hb-react/shared/apps/TopicsIndexV2/pages/TopicsIndexApp'),
);

const TopicShowContainerRoute = ({
  otherTopicContext,
  subdomainNetworkUrl,
  topicId,
  topicMenuRef,
  topicName,
  whitelabelURLEnabled,
}) => {
  const params = useParams();
  // Re-apply values of TopicContext with match.params.topicId if necessary
  const topicContextValue = useMemo(
    () => ({
      ...otherTopicContext,
      subdomainNetworkUrl,
      topicId: whitelabelURLEnabled ? topicId : params.topicId,
      topicMenuRef,
      topicName,
      whitelabelURL: whitelabelURLEnabled,
    }),
    [
      otherTopicContext,
      params,
      topicId,
      topicMenuRef,
      topicName,
      subdomainNetworkUrl,
      whitelabelURLEnabled,
    ],
  );

  return (
    <TopicContext.Provider value={topicContextValue}>
      <TopicShowContainer />
    </TopicContext.Provider>
  );
};

const TopicsAppContainer = (props) => {
  const { page_title: pageTitle, resetingTopics, subdomainNetworkUrl } = props;

  const topicMenuRef = useRef(null);

  // Provider may not be defined at this point, so it will use the default
  // values of the context.
  const {
    whitelabelURL,
    whitelabelName,
    topicId,
    topicName,
    ...otherTopicContext
  } = useTopicContext();
  const { currentUser } = useDefaultAppContexts();

  // Empty useEffect that returns resetingTopics as the cleanup function
  // so it's invoked when the component unmounts
  useEffect(() => resetingTopics, [resetingTopics]);

  // Check if a white label URL exists for the topic and if the current URL matches it
  const isOnWhitelabelURL =
    whitelabelURL && window.location.href.includes(whitelabelName);
  // Never enable whitelabelURL if the user is connected
  const whitelabelURLEnabled = currentUser ? false : isOnWhitelabelURL;

  return (
    <Suspense fallback={null}>
      <TopicRoutes
        pageTitle={pageTitle}
        otherTopicContext={otherTopicContext}
        subdomainNetworkUrl={subdomainNetworkUrl}
        topicId={topicId}
        topicMenuRef={topicMenuRef}
        whitelabelURLEnabled={whitelabelURLEnabled}
        topicName={topicName}
      />
    </Suspense>
  );
};

const TopicRoutes = ({
  otherTopicContext,
  pageTitle,
  subdomainNetworkUrl,
  topicId,
  topicMenuRef,
  whitelabelURLEnabled = false,
  topicName,
}) => {
  if (whitelabelURLEnabled) {
    return (
      <ReactRouterRoutes>
        <Route
          path="*"
          element={
            <TopicShowContainerRoute
              otherTopicContext={otherTopicContext}
              subdomainNetworkUrl={subdomainNetworkUrl}
              topicId={topicId}
              topicMenuRef={topicMenuRef}
              whitelabelURLEnabled={whitelabelURLEnabled}
              topicName={topicName}
            />
          }
        />
      </ReactRouterRoutes>
    );
  }

  const path = otherTopicContext?.standalone ? '/topics/*' : '/*';

  return (
    <ReactRouterRoutes>
      <Route path={path}>
        <Route index element={<TopicsIndexApp pageTitle={pageTitle} />} />
        <Route
          path=":topicId/*"
          element={
            <TopicShowContainerRoute
              otherTopicContext={otherTopicContext}
              subdomainNetworkUrl={subdomainNetworkUrl}
              topicId={topicId}
              topicMenuRef={topicMenuRef}
              whitelabelURLEnabled={whitelabelURLEnabled}
              topicName={topicName}
            />
          }
        />
      </Route>
      <Route path="*" element={<NoMatch />} />
    </ReactRouterRoutes>
  );
};

const mapStateToProps = () => ({});

const mapDispatchToProps = {
  resetingTopics,
};

export default connect(mapStateToProps, mapDispatchToProps)(TopicsAppContainer);
